import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Star, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RatingModal = ({ 
  isOpen, 
  onClose, 
  bookingId, 
  recipientName, 
  recipientType, // 'driver' or 'rider'
  recipientPhoto, // optional avatar URL
  onRatingSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.post(`${API}/ratings`, {
        booking_id: bookingId,
        rating,
        comment: comment.trim() || ""
      }, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      toast.success(`Thanks for rating ${recipientName}!`);
      onRatingSubmitted?.();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit rating';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingLabel = (rating) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Great';
      case 5: return 'Excellent';
      default: return 'Select rating';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="rating-modal">
        <DialogHeader>
          <DialogTitle className="text-center">
            Rate your {recipientType}
          </DialogTitle>
          <DialogDescription className="text-center">
            How was your experience with {recipientName}?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Avatar — surfaced so the user sees who they're rating */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-muted/40 ring-2 ring-primary/30 flex items-center justify-center overflow-hidden" data-testid="rating-recipient-avatar">
              {recipientPhoto ? (
                <img src={recipientPhoto} alt={recipientName || 'recipient'} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-primary">
                  {(recipientName || '?').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>

          {/* Star Rating */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-muted-foreground/30'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              {getRatingLabel(hoveredRating || rating)}
            </p>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Leave a comment (optional)
            </label>
            <Textarea
              placeholder={`Share your experience with ${recipientName}...`}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={500}
              data-testid="rating-comment"
            />
            <p className="text-xs text-muted-foreground text-right">
              {comment.length}/500
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-full"
              disabled={submitting}
            >
              Skip
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 rounded-full bg-primary"
              disabled={submitting || rating === 0}
              data-testid="submit-rating-btn"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Submit Rating
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RatingModal;
